import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Card } from "../components/ui";
import { api, type OptimizerConfigProfile, type ProgramacionAssignResp } from "../data/api";
import { parseCsv } from "../features/programacion/csvParser";
import { batchOrderRowsToSaleRows } from "../features/programacion/orderBatchMapping";
import { effectiveCarrierCode, groupByCarrier } from "../features/programacion/grouping";
import {
  ProgramacionMap,
  selectByPolygon,
} from "../features/programacion/ProgramacionMap";
import {
  useOptimizerConfig,
  type OptimizerConfig,
} from "../features/programacion/optimizerConfig";
import type { LatLon } from "../features/programacion/geometry";
import type {
  CarrierGroup,
  SaleRow,
} from "../features/programacion/types";

// ProgramacionPlanPage — pantalla de planificación: optimizador,
// polígono + drag (UX tipo Roadnet), mapa interactivo, persistencia
// del plan a rutas. Se llega aquí desde la lista de batches en
// `/programacion` (M11). La pantalla soporta dos modos de carga:
//   - Desde batches: navega con state.batchIds → hidrata las órdenes
//     desde DB y guarda los UUIDs para marcarlos como 'planned' al
//     persistir el plan.
//   - Desde archivo: el input de file sigue funcionando como fallback
//     (legacy / pruebas rápidas).
export function ProgramacionPlanPage() {
  const location = useLocation();
  const initialBatchIds = useMemo<string[]>(() => {
    const s = location.state as { batchIds?: unknown } | null;
    if (!s || !Array.isArray(s.batchIds)) return [];
    return s.batchIds.filter((x): x is string => typeof x === "string");
  }, [location.state]);

  // --- Estado del CSV cargado ---
  const [rows, setRows] = useState<SaleRow[] | null>(null);
  // Batches que se están planificando — se setean al cargar desde DB
  // y se usan al guardar para marcarlos como 'planned'. Cuando se
  // carga un archivo CSV manual queda vacío (no hay batch que cerrar).
  const [batchIds, setBatchIds] = useState<string[]>(initialBatchIds);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [skipped, setSkipped] = useState(0);

  // --- Estado interactivo (UI) ---
  const [assignments, setAssignments] = useState<Map<number, string>>(new Map());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [drawingPolygon, setDrawingPolygon] = useState(false);
  const [draftVertices, setDraftVertices] = useState<LatLon[]>([]);
  const [closedPolygon, setClosedPolygon] = useState<LatLon[] | null>(null);
  const [focusCode, setFocusCode] = useState<string | null>(null);

  // --- Asignación dinámica (M10.2) ---
  // assignResult guarda el último output del optimizador. Lo usaremos
  // para renderizar la barra de utilización (M10.3). Persistir asignaciones
  // pasa por `assignments` (mutable por drag/lasso/optimizador).
  const [assignResult, setAssignResult] = useState<ProgramacionAssignResp | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Estado de "Programar rutas" (persiste a DB para Última Milla).
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  // --- No asignados (M10.4) ---
  // IDs que el admin marcó como "no asignar" via lasso → quedan fuera
  // del pool para optimización y no se cuentan en KPIs de carriers.
  const [unassignedIds, setUnassignedIds] = useState<Set<number>>(new Set());
  const [tab, setTab] = useState<"carriers" | "utilization" | "unassigned">("carriers");

  // --- Configuración del optimizador (M10.5) ---
  // Persiste en sessionStorage entre navegaciones. Defaults: 12h /
  // 125% peso / 100% volumen / proxies 0.1 kg-PEN, 0.001 m³-PEN.
  // setConfig se sigue usando para aplicar un perfil del backend; el
  // reset manual quedó obsoleto (cambiar de perfil sirve para "volver al default").
  const [config, setConfig] = useOptimizerConfig();

  // --- Perfiles del optimizador desde DB (M11.5) ---
  // El admin define perfiles en /admin/optimizer-configs. El planner los
  // ve en un dropdown y puede aplicar uno como base; después puede
  // override en los campos del panel (es-temporal-no-pisa-el-perfil).
  const [profiles, setProfiles] = useState<OptimizerConfigProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.listOptimizerConfigs();
        if (cancelled) return;
        setProfiles(list);
        // Auto-aplicar el default — solo en el primer load, no cuando el
        // usuario ya tocó algo. Si no hay default, dejamos los valores
        // sessionStorage / defaults locales como hasta ahora.
        const def = list.find((p) => p.is_default);
        if (def) {
          setSelectedProfileId(def.id);
          setConfig(def.settings);
        }
      } catch {
        // Fallback silencioso: si el endpoint falla seguimos con los
        // defaults locales — modo legacy sin perfiles compartidos.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Solo al montar. Si re-corremos esto en cada change del config
    // entraríamos en loop (setConfig dispara dep change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyProfile = useCallback(
    (id: string) => {
      const p = profiles.find((x) => x.id === id);
      if (!p) return;
      setSelectedProfileId(id);
      setConfig(p.settings);
    },
    [profiles, setConfig],
  );

  // --- Derivado ---
  // Filtramos los unassigned ANTES del group para que no contribuyan
  // a ningún KPI de carrier. El conjunto se sigue mostrando en el mapa
  // pero greyed out + en el tab "No asignados".
  const activeRows = useMemo(() => {
    if (!rows) return null;
    if (unassignedIds.size === 0) return rows;
    return rows.filter((r) => !unassignedIds.has(r.id));
  }, [rows, unassignedIds]);

  const parsed = useMemo(() => {
    if (!activeRows) return null;
    return groupByCarrier(activeRows, assignments, {
      kgPerSol: config.kgPerSol,
      m3PerSol: config.m3PerSol,
    });
  }, [activeRows, assignments, config.kgPerSol, config.m3PerSol]);

  // Lista de no asignados con detalle para el tab.
  const unassignedRows = useMemo(() => {
    if (!rows || unassignedIds.size === 0) return [];
    return rows.filter((r) => unassignedIds.has(r.id));
  }, [rows, unassignedIds]);

  // Programar rutas: persiste el assignResult a DB (routes + route_stops).
  // Estas rutas son las que después aparecen en los módulos de Última
  // Milla (Tracking / Operación / Avance) — son la fuente de verdad
  // para el día. Reusa el endpoint /save legacy que ya hace upserts.
  const onSavePlan = useCallback(async () => {
    if (!parsed || !rows || !assignResult) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      const byId = new Map<number, SaleRow>();
      for (const r of rows) byId.set(r.id, r);

      // Agrupar assignments por carrier, ordenados por sequence.
      const byCarrier = new Map<string, ProgramacionAssignResp["assignments"]>();
      for (const a of assignResult.assignments) {
        const list = byCarrier.get(a.carrier_code) ?? [];
        list.push(a);
        byCarrier.set(a.carrier_code, list);
      }
      for (const list of byCarrier.values()) list.sort((x, y) => x.sequence - y.sequence);

      const today = new Date();
      const plannedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const carriersBody = parsed.carriers
        .filter((c) => c.code !== "" && byCarrier.has(c.code))
        .map((c) => {
          const seq = byCarrier.get(c.code) ?? [];
          const util = assignResult.utilization.find((u) => u.carrier_code === c.code);
          return {
            code: c.code,
            name: c.name,
            // OR-Tools no expone distance/duration por carrier en
            // unidades reales (km/s). Usamos lo que tenemos: time_h.
            distance_m: 0,
            duration_s: (util?.time_h ?? 0) * 3600,
            order: seq
              .map((a) => byId.get(a.row_id))
              .filter((r): r is SaleRow => !!r)
              .map((r) => ({
                id: r.id,
                lat: r.lat,
                lon: r.lon,
                cliente: r.cliente,
                cod_cliente: r.codCliente ?? "",
                valor_venta: r.valorVenta,
              })),
          };
        });

      const resp = await api.programacionSave({
        planned_date: plannedDate,
        carriers: carriersBody,
      });
      // Cerrar el ciclo del batch: si vinimos desde la lista de
      // batches, marcarlos como 'planned' para que no reaparezcan
      // como pendientes. Es best-effort — un fallo acá no rompe el
      // save que ya se hizo, solo lo logueamos.
      let batchSuffix = "";
      if (batchIds.length > 0) {
        const results = await Promise.allSettled(
          batchIds.map((id) => api.updateOrderBatchStatus(id, "planned")),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          batchSuffix = ` · ⚠ ${failed} batch${failed === 1 ? "" : "es"} no se pudo marcar como planned`;
        } else {
          batchSuffix = ` · ${batchIds.length} lote${batchIds.length === 1 ? "" : "s"} marcado${batchIds.length === 1 ? "" : "s"} como planificado${batchIds.length === 1 ? "" : "s"}`;
        }
      }
      setSaveStatus(
        `✓ ${resp.created} ruta${resp.created === 1 ? "" : "s"} programada${resp.created === 1 ? "" : "s"} · ` +
          `${resp.mapped} con chofer · ${resp.unmapped} sin asignar` +
          batchSuffix,
      );
    } catch (e) {
      setSaveStatus(`Error: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }, [parsed, rows, assignResult, batchIds]);

  // Mapeo rowId → sequence (orden de visita 1-indexed) producido por
  // OR-Tools. Lo construimos una vez al cambiar assignResult y lo
  // pasamos al mapa para numerar los markers.
  const sequenceByRowId = useMemo(() => {
    const m = new Map<number, number>();
    if (!assignResult) return m;
    for (const a of assignResult.assignments) {
      m.set(a.row_id, a.sequence);
    }
    return m;
  }, [assignResult]);

  // CV de ventas (PEN) entre carriers — métrica financiera, computada
  // localmente porque no depende del optimizer. Forma parte del set
  // de "variabilidades" junto con peso/vol/horas.
  const salesStats = useMemo(() => {
    if (!parsed || parsed.carriers.length === 0) {
      return { cvPct: 0, mean: 0, maxDelta: 0, maxDeltaCarrier: "" };
    }
    const totals = parsed.carriers.map((c) => c.totalSales);
    const n = totals.length;
    const m = totals.reduce((a, b) => a + b, 0) / n;
    const variance = totals.reduce((acc, v) => acc + (v - m) ** 2, 0) / n;
    const std = Math.sqrt(variance);
    const cv = m > 0 ? (std / m) * 100 : 0;
    let mx = 0;
    let mxCode = "";
    for (const c of parsed.carriers) {
      const d = Math.abs(c.totalSales - m);
      if (d > mx) {
        mx = d;
        mxCode = c.code || "—";
      }
    }
    return { cvPct: cv, mean: m, maxDelta: mx, maxDeltaCarrier: mxCode };
  }, [parsed]);

  // Variabilidad global = promedio de las CVs activas. Ventas siempre
  // entra (es local). Las otras 3 entran si el optimizador corrió y
  // sus toggles están ON. Si no hay assignResult, equivale a CV ventas.
  const variabilityGlobal = useMemo(() => {
    const cvs: number[] = [salesStats.cvPct];
    if (assignResult) {
      cvs.push(assignResult.cv_time_pct);
      if (config.useWeight) cvs.push(assignResult.cv_weight_pct);
      if (config.useVolume) cvs.push(assignResult.cv_volume_pct);
    }
    return cvs.reduce((a, b) => a + b, 0) / cvs.length;
  }, [salesStats.cvPct, assignResult, config.useWeight, config.useVolume]);

  // Re-calcular selección cuando se cierra el polígono.
  useEffect(() => {
    if (!closedPolygon || !rows) return;
    setSelectedIds(selectByPolygon(rows, closedPolygon));
  }, [closedPolygon, rows]);

  // ESC cancela cualquier modo activo (drawing / selección).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrawingPolygon(false);
        setDraftVertices([]);
        setClosedPolygon(null);
        setSelectedIds(new Set());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Hidrata las órdenes desde DB cuando se llegó con batchIds. Sustituye
  // el flujo de "subir CSV" — los batches ya están persistidos. Convertimos
  // BatchOrderRow (snake_case, lat/lon nullable) al shape SaleRow que el
  // resto de la página espera (camelCase, lat/lon numéricos requeridos —
  // las filas sin coords se filtran).
  useEffect(() => {
    if (initialBatchIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setWarnings([]);
      try {
        const dbRows = await api.listOrdersByBatchIds(initialBatchIds);
        if (cancelled) return;
        const { rows: mapped, skipped: skip } = batchOrderRowsToSaleRows(dbRows);
        setRows(mapped);
        setSkipped(skip);
        setFileName(`Batches: ${initialBatchIds.length} lote${initialBatchIds.length === 1 ? "" : "s"}`);
      } catch (e) {
        if (!cancelled) setError(`Error cargando órdenes: ${(e as Error).message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialBatchIds]);

  // --- Handlers ---
  const onFile = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    setWarnings([]);
    setRows(null);
    setAssignments(new Map());
    setSelectedIds(new Set());
    setClosedPolygon(null);
    setDraftVertices([]);
    setDrawingPolygon(false);
    setFocusCode(null);
    setFileName(file.name);
    // Si veníamos de batches, al cargar un archivo manual perdemos
    // esa asociación — los batches quedan sin marcarse como planned.
    setBatchIds([]);
    try {
      const result = await parseCsv(file);
      if (!result.ok) {
        setError(errorMessage(result.error));
        return;
      }
      setWarnings(result.data.warnings.slice(0, 5));
      setRows(result.data.rows);
      // Re-derive para calcular skipped (rows con coords inválidas).
      const initial = groupByCarrier(result.data.rows);
      setSkipped(initial.skipped);
    } catch (e) {
      setError(`Error inesperado: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) onFile(f);
    },
    [onFile],
  );

  const onToggleDrawing = useCallback(() => {
    setDrawingPolygon((prev) => {
      const next = !prev;
      if (next) {
        // Entrar a modo lasso: limpiar estado previo.
        setDraftVertices([]);
        setClosedPolygon(null);
        setSelectedIds(new Set());
      } else {
        setDraftVertices([]);
      }
      return next;
    });
  }, []);

  // Lifecycle del lasso freehand. Empieza al mousedown, va agregando
  // puntos mientras arrastra, y al soltar cerramos el polígono.
  const onLassoStart = useCallback((v: LatLon) => {
    setDraftVertices([v]);
  }, []);
  const onLassoPoint = useCallback((v: LatLon) => {
    setDraftVertices((prev) => [...prev, v]);
  }, []);
  const onLassoEnd = useCallback(() => {
    setDraftVertices((draft) => {
      if (draft.length >= 3) {
        setClosedPolygon(draft.slice());
      }
      return [];
    });
    setDrawingPolygon(false);
  }, []);

  const onClearPolygon = useCallback(() => {
    setClosedPolygon(null);
    setDraftVertices([]);
    setDrawingPolygon(false);
    setSelectedIds(new Set());
  }, []);

  const onAssign = useCallback(
    (targetCode: string) => {
      if (selectedIds.size === 0) return;
      setAssignments((prev) => {
        const next = new Map(prev);
        for (const id of selectedIds) next.set(id, targetCode);
        return next;
      });
      // Tras reasignar, limpiamos selección y polígono.
      setSelectedIds(new Set());
      setClosedPolygon(null);
    },
    [selectedIds],
  );

  const resetAll = useCallback(() => {
    setRows(null);
    setAssignments(new Map());
    setSelectedIds(new Set());
    setClosedPolygon(null);
    setDraftVertices([]);
    setDrawingPolygon(false);
    setFocusCode(null);
    setError(null);
    setFileName(null);
    setUnassignedIds(new Set());
    setTab("carriers");
    setBatchIds([]);
  }, []);

  // --- Handlers no asignados (M10.4) ---
  const onMarkUnassigned = useCallback(() => {
    if (selectedIds.size === 0) return;
    setUnassignedIds((prev) => {
      const next = new Set(prev);
      for (const id of selectedIds) next.add(id);
      return next;
    });
    // Limpiar selección y polígono — la acción se completó.
    setSelectedIds(new Set());
    setClosedPolygon(null);
  }, [selectedIds]);

  const onReinclude = useCallback((id: number) => {
    setUnassignedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const onReincludeAll = useCallback(() => {
    setUnassignedIds(new Set());
  }, []);

  // --- Asignación dinámica (M10.2) ---
  // Llama al endpoint /assign con el state actual. El backend devuelve:
  //   - assignments: rowId → carrierCode (la nueva asignación completa)
  //   - unassigned: ids que no caben en ningún carrier (los marcamos)
  //   - utilization: % peso/vol/tiempo por carrier (para M10.3)
  const onAssignDynamic = useCallback(async () => {
    if (!parsed || !rows || !activeRows) return;
    setAssigning(true);
    setAssignError(null);
    try {
      const body = {
        carriers: parsed.carriers
          .filter((c) => c.code !== "")
          .map((c) => ({ code: c.code })),
        rows: activeRows.map((r) => ({
          id: r.id,
          cod_cliente: r.codCliente,
          lat: r.lat,
          lon: r.lon,
          valor_venta: r.valorVenta,
          peso_kg: r.pesoKg,
          volumen_m3: r.volumenM3,
          has_peso: r.pesoKg !== undefined,
          has_volumen: r.volumenM3 !== undefined,
          current_carrier_code: effectiveCarrierCode(r, assignments),
        })),
        limits: {
          max_hours: config.maxHours,
          max_weight_pct: config.maxWeightPct,
          max_volume_pct: config.maxVolumePct,
          use_weight: config.useWeight,
          use_volume: config.useVolume,
          use_sales: config.useSales,
        },
        fallbacks: {
          service_minutes: config.serviceMinutes,
          kg_per_sol: config.kgPerSol,
          m3_per_sol: config.m3PerSol,
        },
        stability_penalty: config.stabilityPenalty,
      };
      const resp = await api.programacionAssign(body);
      const newAssignments = new Map<number, string>();
      for (const a of resp.assignments) newAssignments.set(a.row_id, a.carrier_code);
      setAssignments(newAssignments);
      setUnassignedIds((prev) => {
        const next = new Set(prev);
        for (const u of resp.unassigned) next.add(u.row_id);
        return next;
      });
      setAssignResult(resp);
      setTab("utilization");
    } catch (e) {
      setAssignError((e as Error).message);
    } finally {
      setAssigning(false);
    }
  }, [parsed, rows, activeRows, assignments, config]);

  // --- Render ---
  if (!rows || !parsed) {
    return <UploadView loading={loading} error={error} fileName={fileName} onInputChange={onInputChange} />;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      {/* Overlay de carga: bloquea TODA la interacción con la página
          mientras corre el optimizer (puede tomar 30-90s en problemas
          grandes). Sin esto, el operador podría reasignar puntos a mano
          o subir otro CSV mientras hay un optimizer corriendo, dejando
          el state inconsistente cuando llega la respuesta. */}
      {(assigning || saving) && <PageBusyOverlay label={assigning ? "Optimizando…" : "Programando rutas…"} />}
      <ResultHeader
        parsed={parsed}
        fileName={fileName}
        warnings={warnings}
        skipped={skipped}
        modifiedCount={assignments.size}
        variabilityGlobal={variabilityGlobal}
        hasOptimizationResult={!!assignResult}
        onReset={resetAll}
      />
      <Toolbar
        drawingPolygon={drawingPolygon}
        selectedCount={selectedIds.size}
        onToggleDrawing={onToggleDrawing}
        onClear={onClearPolygon}
        assigning={assigning}
        assignError={assignError}
        onAssignDynamic={onAssignDynamic}
        assignResult={assignResult}
        saving={saving}
        saveStatus={saveStatus}
        onSavePlan={onSavePlan}
      />
      <KpiBar
        parsed={parsed}
        unassignedCount={unassignedIds.size}
        assignResult={assignResult}
      />
      <div className="flex-1 flex overflow-hidden border-t border-border">
        <aside className="w-[460px] flex flex-col bg-card border-r border-border">
          <ConfigPanel
            config={config}
            profiles={profiles}
            selectedProfileId={selectedProfileId}
            onSelectProfile={applyProfile}
          />
          <LeftPanelTabs
            tab={tab}
            unassignedCount={unassignedIds.size}
            hasUtilization={!!assignResult}
            onChange={setTab}
          />
          {tab === "carriers" && (
            <CarriersTable
              parsed={parsed}
              focusCode={focusCode}
              selectedCount={selectedIds.size}
              onFocus={setFocusCode}
              onDropToCarrier={(code) => onAssign(code)}
            />
          )}
          {tab === "utilization" && (
            <UtilizationView
              utilization={assignResult?.utilization ?? []}
              carriersById={parsed.carriers}
              totalDistanceKm={assignResult?.total_distance_km ?? 0}
              variabilityPct={variabilityGlobal}
              cvSales={salesStats.cvPct}
              cvWeight={assignResult?.cv_weight_pct ?? 0}
              cvVolume={assignResult?.cv_volume_pct ?? 0}
              cvTime={assignResult?.cv_time_pct ?? 0}
              useWeight={config.useWeight}
              useVolume={config.useVolume}
              onFocus={setFocusCode}
              focusCode={focusCode}
            />
          )}
          {tab === "unassigned" && (
            <UnassignedList
              rows={unassignedRows}
              onReinclude={onReinclude}
              onReincludeAll={onReincludeAll}
            />
          )}
        </aside>
        <div className="flex-1">
          <ProgramacionMap
            rows={rows}
            carriers={parsed.carriers}
            assignments={assignments}
            selectedIds={selectedIds}
            unassignedIds={unassignedIds}
            drawingPolygon={drawingPolygon}
            closedPolygon={closedPolygon}
            draftVertices={draftVertices}
            onLassoStart={onLassoStart}
            onLassoPoint={onLassoPoint}
            onLassoEnd={onLassoEnd}
            onAssign={onAssign}
            onMarkUnassigned={onMarkUnassigned}
            sequenceByRowId={sequenceByRowId}
            focusCode={focusCode}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------- subcomponents ----------------

// Overlay que cubre toda la página mientras hay una operación bloqueante
// en curso (optimizar o guardar). El backdrop blured + pointer-events
// auto impide cualquier interacción debajo. Aparece centrado un spinner
// circular CSS-only + el label dinámico que pasó el caller.
function PageBusyOverlay({ label }: { label: string }) {
  // position:fixed + z muy alto para cubrir TODA la ventana incluido el
  // mapa Leaflet (cuyos panes/controles usan z-index 400-1000 internos).
  // Con z-50 el mapa quedaba por encima del overlay; z-[3000] lo tapa.
  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-page/70 backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3 bg-card border border-border rounded-lg px-8 py-6 shadow-lg">
        <div
          className="w-12 h-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin"
          aria-hidden
        />
        <div className="text-sm text-ink font-medium">{label}</div>
        <div className="text-[11px] text-sub max-w-xs text-center">
          Esto puede tomar entre 20 segundos y 2 minutos según el tamaño del problema. No cierres la pestaña.
        </div>
      </div>
    </div>
  );
}

function UploadView({
  loading,
  error,
  fileName,
  onInputChange,
}: {
  loading: boolean;
  error: string | null;
  fileName: string | null;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <Card padding="lg" className="max-w-lg w-full">
        <h1 className="text-lg font-semibold text-ink mb-2">Programación de rutas</h1>
        <p className="text-sm text-sub mb-4">
          Subí el CSV con las ventas del día. Cada fila es una venta a un cliente,
          asignada a un transportista. Vamos a agrupar por código (los dígitos al
          final del nombre) y mostrarlo en mapa. Después podés re-asignar puntos
          entre transportistas con polígono + drag.
        </p>
        <ul className="text-xs text-sub mb-4 list-disc pl-5 space-y-1">
          <li>Columnas requeridas: <code>TRANSPORTISTA</code>, <code>CLIENTE</code>, <code>EA.LATITUD</code>, <code>EA.LONGITUD</code>, <code>Suma de VALOR VENTA</code>.</li>
          <li>Desde Numbers/Excel exportá a <strong>CSV</strong> (Archivo → Exportar a → CSV).</li>
        </ul>
        <label
          className={`block border-2 border-dashed rounded-lg px-4 py-8 text-center cursor-pointer transition-colors ${
            loading ? "opacity-60 pointer-events-none" : "border-border hover:border-primary hover:bg-primary/5"
          }`}
        >
          <input type="file" accept=".csv,text/csv" onChange={onInputChange} className="hidden" />
          <div className="text-3xl mb-2">📄</div>
          <div className="text-sm text-ink font-medium">
            {loading ? "Procesando…" : "Click para seleccionar CSV"}
          </div>
          {fileName && !loading && (
            <div className="text-xs text-sub mt-1">Último: {fileName}</div>
          )}
        </label>
        {error && (
          <div className="mt-4 p-3 rounded bg-state-failed/10 border border-state-failed text-state-failed text-sm">
            {error}
          </div>
        )}
      </Card>
    </div>
  );
}

function ResultHeader({
  parsed,
  fileName,
  warnings,
  skipped,
  modifiedCount,
  variabilityGlobal,
  hasOptimizationResult,
  onReset,
}: {
  parsed: ReturnType<typeof groupByCarrier>;
  fileName: string | null;
  warnings: string[];
  skipped: number;
  modifiedCount: number;
  variabilityGlobal: number;
  hasOptimizationResult: boolean;
  onReset: () => void;
}) {
  // Color del KPI según la salud de la distribución.
  //   <20%  → verde (bien balanceada)
  //   20-50% → ámbar (regular)
  //   >50%  → rojo (muy desbalanceada)
  const cvColor =
    variabilityGlobal < 20
      ? "text-state-delivered"
      : variabilityGlobal < 50
        ? "text-state-delayed"
        : "text-state-failed";

  return (
    <header className="px-6 py-3 bg-card flex items-center justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-base font-semibold text-ink">
          Programación · {parsed.carriers.length} transportistas · {parsed.totalRows} ventas
          {modifiedCount > 0 && (
            <span className="ml-2 text-secondary font-normal text-sm">
              · {modifiedCount} reasignación{modifiedCount === 1 ? "" : "es"}
            </span>
          )}
        </h1>
        <p className="text-xs text-sub mt-0.5">
          {fileName ?? "—"}
          {skipped > 0 && (
            <span className="ml-2 text-state-delayed">
              · {skipped} fila{skipped === 1 ? "" : "s"} sin coordenadas
            </span>
          )}
        </p>
        {warnings.length > 0 && (
          <details className="mt-1">
            <summary className="text-xs text-state-delayed cursor-pointer">
              {warnings.length} aviso{warnings.length === 1 ? "" : "s"}
            </summary>
            <ul className="text-xs text-sub mt-1 list-disc pl-5">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {/* Variabilidad global: promedio de las CVs activas (ventas + las
          dimensiones operativas del optimizer si corrió). Antes era solo
          la de ventas, lo cual confundía respecto del tab Utilización. */}
      <div className="flex items-center gap-4">
        <div className="text-right border-r border-border pr-4">
          <div
            className="text-[10px] uppercase tracking-wide text-sub"
            title="Promedio de las variabilidades activas: ventas + (peso/volumen/horas si corriste el optimizador). Drill-down en el tab Utilización."
          >
            Variabilidad global
          </div>
          <div className={`text-lg font-semibold font-mono ${cvColor}`}>
            {variabilityGlobal.toFixed(1)}%
          </div>
          <div className="text-[10px] text-sub">
            {hasOptimizationResult ? "ventas + horas + cap" : "solo ventas"}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onReset}>
          Subir otro
        </Button>
      </div>
    </header>
  );
}

// Barra de KPIs operativos sobre el mapa+tabla. Resumen rápido para el
// planner: cuántos vehículos están en uso, cuántas órdenes quedaron sin
// asignar, distancia y tiempo promedio recorridos. Los dos últimos solo
// tienen valor real tras correr el optimizador — antes muestran "—".
function KpiBar({
  parsed,
  unassignedCount,
  assignResult,
}: {
  parsed: ReturnType<typeof groupByCarrier>;
  unassignedCount: number;
  assignResult: ProgramacionAssignResp | null;
}) {
  const validCarriers = parsed.carriers.filter((c) => c.code !== "");
  const totalCarriers = validCarriers.length;
  const assignedCarriers = validCarriers.filter((c) => c.rows.length > 0).length;
  const totalRows = parsed.totalRows;

  // Distancia y tiempo promedio se calculan solo sobre carriers que
  // realmente tienen paradas; un carrier vacío no aporta ni "diluye".
  let avgDistanceKm: number | null = null;
  let avgTimeH: number | null = null;
  if (assignResult && assignResult.utilization.length > 0) {
    const withStops = assignResult.utilization.filter((u) => u.stops > 0);
    if (withStops.length > 0) {
      avgDistanceKm = assignResult.total_distance_km / withStops.length;
      avgTimeH = withStops.reduce((a, u) => a + u.time_h, 0) / withStops.length;
    }
  }

  return (
    <div className="px-6 py-2 bg-card border-b border-border flex items-center gap-x-2 gap-y-1 text-sm flex-wrap">
      <Kpi
        icon="🚚"
        label="Vehículos"
        value={`${assignedCarriers}/${totalCarriers}`}
      />
      <Sep />
      <Kpi
        icon="📋"
        label="Órdenes sin asignar"
        value={`${unassignedCount}/${totalRows}`}
        accent={unassignedCount > 0}
      />
      <Sep />
      <Kpi
        icon="🛣️"
        label="Distancia prom."
        value={avgDistanceKm != null ? `${avgDistanceKm.toFixed(1)} km` : "—"}
      />
      <Sep />
      <Kpi
        icon="⏱️"
        label="Tiempo prom."
        value={avgTimeH != null ? formatHM(avgTimeH) : "—"}
      />
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  accent,
}: {
  icon: string;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 px-2">
      <span aria-hidden className="text-base leading-none">
        {icon}
      </span>
      <span className="text-sub">{label}</span>
      <span
        className={`font-semibold font-mono ${accent ? "text-state-delayed" : "text-ink"}`}
      >
        {value}
      </span>
    </div>
  );
}

function Sep() {
  return <span className="h-5 w-px bg-border" aria-hidden />;
}

// Formatea horas decimales como "H:MM hrs". 7.62 → "7:37 hrs".
function formatHM(h: number): string {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}:${mm.toString().padStart(2, "0")} hrs`;
}

function Toolbar({
  drawingPolygon,
  selectedCount,
  onToggleDrawing,
  onClear,
  assigning,
  assignError,
  onAssignDynamic,
  assignResult,
  saving,
  saveStatus,
  onSavePlan,
}: {
  drawingPolygon: boolean;
  selectedCount: number;
  onToggleDrawing: () => void;
  onClear: () => void;
  assigning: boolean;
  assignError: string | null;
  onAssignDynamic: () => void;
  assignResult: ProgramacionAssignResp | null;
  saving: boolean;
  saveStatus: string | null;
  onSavePlan: () => void;
}) {
  return (
    <div className="px-6 py-2 bg-page border-t border-border flex items-center gap-3 text-sm flex-wrap">
      <Button
        variant={drawingPolygon ? "primary" : "secondary"}
        size="sm"
        onClick={onToggleDrawing}
      >
        {drawingPolygon ? "Cancelar lasso" : "✏️ Lasso"}
      </Button>
      {drawingPolygon && (
        <span className="text-xs text-sub">
          Apretá y arrastrá para rodear · soltá para cerrar
        </span>
      )}
      {selectedCount > 0 && !drawingPolygon && (
        <>
          <span className="text-xs text-ink">
            <strong>{selectedCount}</strong> punto{selectedCount === 1 ? "" : "s"} seleccionado{selectedCount === 1 ? "" : "s"}
          </span>
          <Button variant="ghost" size="sm" onClick={onClear}>
            Limpiar
          </Button>
        </>
      )}

      <span className="mx-2 h-5 w-px bg-border" />

      <Button
        variant="primary"
        size="sm"
        onClick={onAssignDynamic}
        disabled={assigning}
        title="Asignación optimizada con OR-Tools (solver industrial de Google). Respeta peso/volumen/tiempo del panel."
      >
        {assigning ? "Optimizando…" : "⚡ Optimizar asignación"}
      </Button>
      {assignResult && !assigning && (
        <span
          className="text-xs text-sub"
          title={`OR-Tools · ${(assignResult.duration_ms / 1000).toFixed(1)}s`}
        >
          ✓ {assignResult.changes_from_initial} cambio{assignResult.changes_from_initial === 1 ? "" : "s"}
          {assignResult.unassigned.length > 0 && ` · ${assignResult.unassigned.length} sin cupo`}
          {` · ${(assignResult.duration_ms / 1000).toFixed(1)}s`}
        </span>
      )}
      {assignError && (
        <span className="text-xs text-state-failed">{assignError}</span>
      )}

      <span className="mx-2 h-5 w-px bg-border" />

      {/* "Programar rutas planificadas": persiste assignResult a DB.
          Solo habilitado tras correr el optimizer (sino no hay nada
          que guardar). Una vez guardado, las rutas aparecen en
          Última Milla (Tracking / Operación / Avance). */}
      <Button
        variant="primary"
        size="sm"
        onClick={onSavePlan}
        disabled={!assignResult || saving || assigning}
        title={
          !assignResult
            ? "Corré primero el optimizador"
            : "Guarda las rutas en la base. A partir de ahí aparecen en los módulos de Última milla."
        }
      >
        {saving ? "Programando…" : "📋 Programar rutas planificadas"}
      </Button>
      {saveStatus && (
        <span
          className={`text-xs ${saveStatus.startsWith("✓") ? "text-state-delivered" : "text-state-failed"}`}
        >
          {saveStatus}
        </span>
      )}

      <span className="ml-auto text-xs text-sub">ESC limpia la selección</span>
    </div>
  );
}

function CarriersTable({
  parsed,
  focusCode,
  selectedCount,
  onFocus,
  onDropToCarrier,
}: {
  parsed: ReturnType<typeof groupByCarrier>;
  focusCode: string | null;
  selectedCount: number;
  onFocus: (code: string | null) => void;
  onDropToCarrier: (code: string) => void;
}) {
  const totals = useMemo(() => {
    return parsed.carriers.reduce(
      (acc, c) => ({
        sales: acc.sales + c.totalSales,
        clients: acc.clients + c.uniqueClients,
        weight: Math.round((acc.weight + c.totalWeightKg) * 10) / 10,
      }),
      { sales: 0, clients: 0, weight: 0 },
    );
  }, [parsed.carriers]);

  const money = new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" });

  return (
    <div className="overflow-auto flex-1">
      <table className="text-sm min-w-[560px]">
          <thead className="bg-page text-sub border-b border-border sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wide font-medium whitespace-nowrap">
                Transportista
              </th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wide font-medium whitespace-nowrap">
                Ventas
              </th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wide font-medium whitespace-nowrap">
                Clientes
              </th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wide font-medium whitespace-nowrap">
                Peso
              </th>
            </tr>
          </thead>
          <tbody>
            {parsed.carriers.map((c) => (
              <CarrierRow
                key={c.code || `__${c.name}`}
                c={c}
                selected={c.code === focusCode}
                hasSelection={selectedCount > 0}
                money={money}
                onClick={() => onFocus(c.code === focusCode ? null : c.code)}
                onDrop={() => onDropToCarrier(c.code)}
              />
            ))}
          </tbody>
          <tfoot className="bg-page sticky bottom-0 border-t border-border">
            <tr className="font-semibold">
              <td className="px-3 py-2 text-ink whitespace-nowrap">Total</td>
              <td className="px-3 py-2 text-right font-mono text-xs text-ink whitespace-nowrap">
                {money.format(totals.sales)}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs text-ink whitespace-nowrap">
                {totals.clients}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs text-ink whitespace-nowrap">
                {totals.weight.toFixed(1)} kg
              </td>
            </tr>
          </tfoot>
        </table>
    </div>
  );
}

function CarrierRow({
  c,
  selected,
  hasSelection,
  money,
  onClick,
  onDrop,
}: {
  c: CarrierGroup;
  selected: boolean;
  hasSelection: boolean;
  money: Intl.NumberFormat;
  onClick: () => void;
  onDrop: () => void;
}) {
  const [hoverDrop, setHoverDrop] = useState(false);
  return (
    <tr
      className={`border-b border-border cursor-pointer transition-colors ${
        selected ? "bg-primary/5" : hoverDrop ? "bg-secondary/10" : "hover:bg-page/60"
      }`}
      onClick={onClick}
      onMouseEnter={() => hasSelection && setHoverDrop(true)}
      onMouseLeave={() => setHoverDrop(false)}
      onMouseUp={() => {
        if (hasSelection) onDrop();
        setHoverDrop(false);
      }}
      title={hasSelection ? `Soltar acá → mover selección a ${c.code || "sin código"}` : undefined}
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full shrink-0"
            style={{ background: c.color }}
          />
          <div>
            <div className="text-ink font-medium whitespace-nowrap">
              {c.code || "Sin código"}
            </div>
            <div className="text-xs text-sub whitespace-nowrap">
              {c.name}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">
        {money.format(c.totalSales)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">
        {c.uniqueClients}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">
        {c.totalWeightKg.toFixed(1)} kg
      </td>
    </tr>
  );
}

// ---------------- left panel: selector de perfil + tabs ----------------
//
// El panel inline editable se removió (M11.5): toda la configuración del
// optimizador vive ahora en /admin/optimizer-configs. Esta sección solo
// elige qué perfil aplicar a la planificación actual y muestra el
// resumen del seleccionado para que el planner vea bajo qué parámetros
// va a correr.

function ConfigPanel({
  config,
  profiles,
  selectedProfileId,
  onSelectProfile,
}: {
  config: OptimizerConfig;
  profiles: OptimizerConfigProfile[];
  selectedProfileId: string | null;
  onSelectProfile: (id: string) => void;
}) {
  const selectedProfile = profiles.find((p) => p.id === selectedProfileId) ?? null;
  return (
    <div className="border-b border-border bg-card">
      <div className="px-4 py-2 flex items-center gap-2">
        <span className="text-xs text-sub shrink-0">Configuración:</span>
        {profiles.length > 0 ? (
          <select
            value={selectedProfileId ?? ""}
            onChange={(e) => onSelectProfile(e.target.value)}
            className="flex-1 text-sm border border-border rounded px-2 py-1 bg-card"
          >
            {!selectedProfileId && <option value="">— Sin perfil —</option>}
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.is_default ? " (default)" : ""}
              </option>
            ))}
          </select>
        ) : (
          <span className="flex-1 text-xs text-sub italic">Sin perfiles disponibles</span>
        )}
        <Link
          to="/admin/optimizer-configs"
          title="Gestionar perfiles"
          className="text-xs text-primary hover:text-primary-hover shrink-0"
        >
          Gestionar →
        </Link>
      </div>
      {/* Resumen compacto de los valores activos. Es lectura, no edición:
          si el planner quiere ajustar, va a /admin/optimizer-configs. */}
      <div className="px-4 pb-2 -mt-1">
        <div className="text-[10px] uppercase tracking-wide text-sub mb-1">
          {selectedProfile?.description || "Parámetros aplicados"}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-ink font-mono">
          <span>{config.maxHours}h jornada</span>
          <span>{config.serviceMinutes}min/cliente</span>
          <span className={config.useWeight ? "" : "text-sub line-through"}>
            Peso {config.maxWeightPct}%
          </span>
          <span className={config.useVolume ? "" : "text-sub line-through"}>
            Vol {config.maxVolumePct}%
          </span>
          {config.useSales && <span>Balance ventas</span>}
          <span>Estabilidad {config.stabilityPenalty}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------- left panel: tabs + unassigned list ----------------

type TabId = "carriers" | "utilization" | "unassigned";

function LeftPanelTabs({
  tab,
  unassignedCount,
  hasUtilization,
  onChange,
}: {
  tab: TabId;
  unassignedCount: number;
  hasUtilization: boolean;
  onChange: (t: TabId) => void;
}) {
  const tabs: { id: TabId; label: string; badge?: number; disabled?: boolean; title?: string }[] = [
    { id: "carriers", label: "Transportistas" },
    {
      id: "utilization",
      label: "Utilización",
      disabled: !hasUtilization,
      title: hasUtilization ? undefined : "Corré 'Optimizar asignación' para ver utilización",
    },
    { id: "unassigned", label: "No asignados", badge: unassignedCount },
  ];
  return (
    <nav className="flex border-b border-border bg-page text-sm">
      {tabs.map((t) => {
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            onClick={() => !t.disabled && onChange(t.id)}
            disabled={t.disabled}
            title={t.title}
            className={`px-3 py-2 border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              active
                ? "border-primary text-ink font-medium bg-card"
                : t.disabled
                  ? "border-transparent text-sub/40 cursor-not-allowed"
                  : "border-transparent text-sub hover:text-ink"
            }`}
          >
            {t.label}
            {typeof t.badge === "number" && t.badge > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                active ? "bg-primary text-white" : "bg-state-failed/15 text-state-failed"
              }`}>
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

function UnassignedList({
  rows,
  onReinclude,
  onReincludeAll,
}: {
  rows: SaleRow[];
  onReinclude: (id: number) => void;
  onReincludeAll: () => void;
}) {
  const money = new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" });
  if (rows.length === 0) {
    return (
      <div className="flex-1 p-6 text-center text-sub text-sm">
        <div className="text-3xl mb-2">✓</div>
        <div>Ningún cliente está marcado como no asignar.</div>
        <div className="text-xs mt-2 text-sub/70">
          Usá el lasso para seleccionar puntos en el mapa y aparecerá un chip
          para excluirlos.
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="px-4 py-2 bg-page text-xs text-sub flex items-center justify-between sticky top-0 border-b border-border">
        <span><strong className="text-ink">{rows.length}</strong> cliente{rows.length === 1 ? "" : "s"} excluido{rows.length === 1 ? "" : "s"}</span>
        <button
          onClick={onReincludeAll}
          className="text-primary hover:underline"
        >
          Reincluir todos
        </button>
      </div>
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={r.id} className="px-4 py-2 flex items-center gap-2 hover:bg-page/60">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink truncate">
                {r.cliente}
              </div>
              <div className="text-xs text-sub truncate">
                {r.codCliente ? `${r.codCliente} · ` : ""}
                {money.format(r.valorVenta || 0)}
              </div>
            </div>
            <button
              onClick={() => onReinclude(r.id)}
              className="text-xs text-primary hover:underline whitespace-nowrap"
              title="Volver a incluir en el pool"
            >
              ↻ Reincluir
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Umbrales del semáforo de utilización:
//   <30%    → gris (sub-utilizado, "el chofer va con el camión vacío")
//   30-70%  → verde claro (saludable, holgura cómoda)
//   70-90%  → verde fuerte (eficiencia ideal)
//   90-100% → amarillo (cerca del límite, vigilar)
//   >100%   → rojo (sobrecargado — solo aparece si los % del config lo permiten)
function utilizationLevel(pct: number): {
  label: string;
  bgClass: string;     // color de la barra rellena
  trackClass: string;  // color de fondo (el riel)
  textClass: string;   // color del texto del %
} {
  if (pct > 100) {
    return {
      label: "sobrecargado",
      bgClass: "bg-state-failed",
      trackClass: "bg-state-failed/15",
      textClass: "text-state-failed",
    };
  }
  if (pct >= 90) {
    return {
      label: "al límite",
      bgClass: "bg-state-delayed",
      trackClass: "bg-state-delayed/15",
      textClass: "text-state-delayed",
    };
  }
  if (pct >= 70) {
    return {
      label: "ideal",
      bgClass: "bg-state-delivered",
      trackClass: "bg-state-delivered/15",
      textClass: "text-state-delivered",
    };
  }
  if (pct >= 30) {
    return {
      label: "holgado",
      bgClass: "bg-state-delivered/60",
      trackClass: "bg-state-delivered/10",
      textClass: "text-state-delivered/80",
    };
  }
  return {
    label: "sub-utilizado",
    bgClass: "bg-sub/40",
    trackClass: "bg-sub/10",
    textClass: "text-sub",
  };
}

function UtilBar({ label, pct }: { label: string; pct: number }) {
  const lvl = utilizationLevel(pct);
  // La barra se llena de 0 a 100%; sobrecargas se renderean con un
  // overflow rojo encima del 100% (visualmente "saturado").
  const fillWidth = Math.min(pct, 100);
  return (
    <div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-sub">{label}</span>
        <span className={`font-mono ${lvl.textClass}`}>{pct.toFixed(0)}%</span>
      </div>
      <div className={`h-1.5 rounded-full overflow-hidden ${lvl.trackClass}`}>
        <div
          className={`h-full transition-all ${lvl.bgClass}`}
          style={{ width: `${fillWidth}%` }}
        />
      </div>
    </div>
  );
}

function UtilizationView({
  utilization,
  carriersById,
  totalDistanceKm,
  variabilityPct,
  cvSales,
  cvWeight,
  cvVolume,
  cvTime,
  useWeight,
  useVolume,
  onFocus,
  focusCode,
}: {
  utilization: ProgramacionAssignResp["utilization"];
  carriersById: CarrierGroup[];
  totalDistanceKm: number;
  variabilityPct: number;
  cvSales: number;
  cvWeight: number;
  cvVolume: number;
  cvTime: number;
  useWeight: boolean;
  useVolume: boolean;
  onFocus: (code: string | null) => void;
  focusCode: string | null;
}) {
  if (utilization.length === 0) {
    return (
      <div className="flex-1 p-6 text-center text-sub text-sm">
        <div className="text-3xl mb-2">📊</div>
        <div>Corré <strong>Optimizar asignación</strong> para ver el % de carga por transportista.</div>
      </div>
    );
  }
  // Indexar carriers por code para sacar el name + color que ya tenemos
  // del CSV (no queremos duplicar esa info en la respuesta del backend).
  const carrierByCode = new Map(carriersById.map((c) => [c.code, c]));
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="px-4 py-2 bg-page sticky top-0 border-b border-border space-y-1">
        <div className="text-xs text-sub flex items-center justify-between">
          <span>
            Distancia: <strong className="text-ink font-mono">{totalDistanceKm.toFixed(1)} km</strong>
          </span>
          <span>
            Variabilidad global: <strong className="text-ink font-mono">{variabilityPct.toFixed(1)}%</strong>
          </span>
        </div>
        <div className="text-[10px] text-sub flex items-center gap-3 font-mono flex-wrap">
          <span title="Coeficiente de variación del total facturado (S/) entre carriers">
            CV ventas: <strong className="text-ink">{cvSales.toFixed(1)}%</strong>
          </span>
          {useWeight && (
            <span title="Coeficiente de variación del peso entre carriers">
              CV peso: <strong className="text-ink">{cvWeight.toFixed(1)}%</strong>
            </span>
          )}
          {useVolume && (
            <span title="Coeficiente de variación del volumen entre carriers">
              CV vol: <strong className="text-ink">{cvVolume.toFixed(1)}%</strong>
            </span>
          )}
          <span title="Coeficiente de variación de horas trabajadas">
            CV horas: <strong className="text-ink">{cvTime.toFixed(1)}%</strong>
          </span>
        </div>
      </div>
      <ul className="divide-y divide-border">
        {utilization.map((u) => {
          const carrier = carrierByCode.get(u.carrier_code);
          const isFocused = focusCode === u.carrier_code;
          return (
            <li
              key={u.carrier_code}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                isFocused ? "bg-primary/10" : "hover:bg-page/60"
              }`}
              onClick={() => onFocus(isFocused ? null : u.carrier_code)}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-block w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: carrier?.color ?? "#90A4AE" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink truncate">
                    {carrier?.name ?? `Transportista ${u.carrier_code}`}
                  </div>
                  <div className="text-[10px] text-sub">
                    {u.stops} parada{u.stops === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                {u.max_kg >= 9_999_999 ? (
                  <div className="text-[11px] text-sub">
                    Peso <span className="font-mono text-ink">{u.load_kg.toFixed(0)} kg</span>
                    <span className="ml-1 text-sub/70 italic">(sin restricción)</span>
                  </div>
                ) : (
                  <UtilBar label={`Peso ${u.load_kg.toFixed(0)} / ${u.max_kg.toFixed(0)} kg`} pct={u.weight_pct} />
                )}
                {u.max_m3 >= 9_999_999 ? (
                  <div className="text-[11px] text-sub">
                    Volumen <span className="font-mono text-ink">{u.load_m3.toFixed(2)} m³</span>
                    <span className="ml-1 text-sub/70 italic">(sin restricción)</span>
                  </div>
                ) : (
                  <UtilBar label={`Volumen ${u.load_m3.toFixed(2)} / ${u.max_m3.toFixed(1)} m³`} pct={u.volume_pct} />
                )}
                <UtilBar label={`Tiempo ${u.time_h.toFixed(1)} / ${u.max_hours.toFixed(0)} h`} pct={u.time_pct} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------- helpers ----------------

function errorMessage(err: { kind: string; missing?: string[]; message?: string }): string {
  switch (err.kind) {
    case "missing-headers":
      return `Faltan columnas en el CSV: ${(err.missing ?? []).join(", ")}.`;
    case "no-data":
      return "El archivo no tiene filas de datos.";
    case "papaparse":
      return `Error parseando el CSV: ${err.message ?? "desconocido"}.`;
    default:
      return "Error desconocido al procesar el archivo.";
  }
}
